
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuthStore } from "../../Store/auth";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ShieldAlert, UploadCloud, CheckCircle2, Clock, AlertTriangle, RefreshCcw } from "lucide-react";
import { toast } from "react-toastify";
import { API_ORIGIN } from "../../config/api";

const API = API_ORIGIN;

const deriveVerification = (coach) => {
  const status =
    coach?.verificationStatus ||
    coach?.status ||
    (coach?.isVerified === true ? "verified" : coach?.isVerified === false ? "unverified" : undefined);
  const note = coach?.verificationNotes || coach?.verifyReason || coach?.rejectionReason || "";
  const m = String(status || "").toLowerCase();
  if (m.includes("verified") || m === "approved") return { state: "verified", label: "Verified", note };
  if (m === "pending" || m === "review") return { state: "pending", label: "Pending Review", note };
  if (m === "rejected" || m === "unverified" || m === "blocked") return { state: "rejected", label: "Rejected", note };
  return { state: "unknown", label: "Unknown", note };
};

const MAX_MB = 10;

export function Verification() {
  const { token, coachId, logout } = useAuthStore();
  const navigate = useNavigate();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [loading, setLoading] = useState(true);
  const [coach, setCoach] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Files
  const [idFront, setIdFront] = useState(null);
  const [idBack, setIdBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [certs, setCerts] = useState([]);

  const v = deriveVerification(coach);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/Coaches/${coachId}`, auth);
        if (!mounted) return;
        setCoach(data);
      } catch (e) {
        if (e?.response?.status === 401) logout?.();
        toast.error("Failed to load verification status");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => (mounted = false);
  }, [coachId, auth, logout]);

  const validate = () => {
    const over = (f) => f && f.size > MAX_MB * 1024 * 1024;
    if (over(idFront) || over(idBack) || over(selfie) || certs.some(over)) {
      toast.error(`Each file must be \u2264 ${MAX_MB}MB`);
      return false;
    }
    if (!idFront || !selfie) {
      toast.error("Please upload at least ID front and a selfie");
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("coachId", coachId);
      if (idFront) fd.append("idFront", idFront);
      if (idBack) fd.append("idBack", idBack);
      if (selfie) fd.append("selfie", selfie);
      certs.forEach((c, i) => fd.append("certificates", c));

      // 🔧 Wire this endpoint on your backend
      await axios.post(`${API}/api/Verification/submit`, fd, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${token}` },
      });

      toast.success("Verification submitted. We'll review shortly.");
      // Optimistic state
      setCoach((c) => ({ ...(c || {}), verificationStatus: "pending" }));
      setIdFront(null); setIdBack(null); setSelfie(null); setCerts([]);
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to submit verification");
    } finally {
      setSubmitting(false);
    }
  };

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/api/Coaches/${coachId}`, auth);
      setCoach(data);
      toast.success("Status updated");
    } catch (e) {
      toast.error("Couldn't refresh status");
    }
  };

  const disabled = v.state === "pending" || v.state === "verified";

  const tone = v.state === "verified" ? "text-emerald-700" : v.state === "pending" ? "text-amber-700" : v.state === "rejected" ? "text-rose-700" : "text-gray-700";
  const icon = v.state === "verified" ? <CheckCircle2 className="w-5 h-5" /> : v.state === "pending" ? <Clock className="w-5 h-5" /> : v.state === "rejected" ? <AlertTriangle className="w-5 h-5" /> : <ShieldAlert className="w-5 h-5" />;

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <ShieldCheck className="w-5 h-5" />
        <h1 className="text-2xl sm:text-3xl font-extrabold">Verification</h1>
        <button onClick={refresh} className="ml-auto inline-flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg border hover:bg-gray-50">
          <RefreshCcw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* Status card */}
      <div className={`rounded-2xl border p-4 ${v.state === "verified" ? "border-emerald-200 bg-emerald-50" : v.state === "pending" ? "border-amber-200 bg-amber-50" : v.state === "rejected" ? "border-rose-200 bg-rose-50" : "border-gray-200 bg-white"}`}>
        <div className={`flex items-center gap-2 font-semibold ${tone}`}>{icon}<span>{v.label}</span></div>
        {v.note ? <div className="text-sm text-gray-700 mt-1">{v.note}</div> : null}
      </div>

      {/* Upload form */}
      <div className="mt-6 bg-white rounded-2xl border p-4">
        <h3 className="font-semibold mb-3">Documents</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Government ID (front) *</label>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setIdFront(e.target.files?.[0] || null)} disabled={disabled} className="block w-full text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Government ID (back)</label>
            <input type="file" accept="image/*,application/pdf" onChange={(e) => setIdBack(e.target.files?.[0] || null)} disabled={disabled} className="block w-full text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Selfie holding ID *</label>
            <input type="file" accept="image/*" onChange={(e) => setSelfie(e.target.files?.[0] || null)} disabled={disabled} className="block w-full text-sm" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Certificates (optional, up to 5)</label>
            <input multiple type="file" accept="image/*,application/pdf" onChange={(e) => setCerts(Array.from(e.target.files || []).slice(0,5))} disabled={disabled} className="block w-full text-sm" />
          </div>
        </div>
        <div className="text-xs text-gray-500 mt-2">Max {MAX_MB}MB per file. Accepted: JPG, PNG, PDF.</div>

        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={submit} disabled={disabled || submitting} className={`px-4 py-2 rounded-lg text-white inline-flex items-center gap-2 ${disabled ? "bg-gray-400 cursor-not-allowed" : "bg-gray-900 hover:bg-black"}`}>
            <UploadCloud className="w-4 h-4" /> {submitting ? "Submitting…" : v.state === "rejected" ? "Resubmit for review" : "Submit for review"}
          </button>
          <button onClick={() => navigate("/dashboard/settings")} className="px-3 py-2 rounded-lg border hover:bg-gray-50 text-sm">Update profile</button>
        </div>
      </div>

      {/* Timeline */}
      <div className="mt-6 bg-white rounded-2xl border p-4">
        <h3 className="font-semibold mb-3">Verification steps</h3>
        <ol className="space-y-2 text-sm text-gray-700">
          <li>1. Submit documents (ID front, selfie, certificates optional)</li>
          <li>2. PTfinderNow team reviews your submission</li>
          <li>3. You receive an email & in-app status update</li>
          <li>4. Once verified, a green badge appears on your profile</li>
        </ol>
      </div>
    </div>
  );
}
